import { useEffect, useState, type ReactNode } from 'react';
import type { Herramienta, Tramo } from '../../api/ops/asistente';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { Interruptor } from '../../componentes/Interruptor';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { useCabecera } from '../../marco/cabecera';
import type { PropsApartado } from './comun';
import {
    admiteAsistente,
    DIAS,
    errorDeTramo,
    gruposDeHerramientas,
    horaDe,
    HORAS_DEL_DIA,
    iconoCanal,
    iconoDeEfecto,
    IDIOMAS,
    lineaDeHerramienta,
    nombreCanal,
    nombreDeHerramienta,
    nombreTono,
    resumenAtencion,
    resumenCanales,
    resumenIdiomas,
    textoCaducidad,
    textoTramosDelDia,
    TONOS,
    tramosPorDia,
} from './reglas';
import { AYUDA } from './textos';

/**
 * Configuración (11.5): «Cómo se presenta» (nombre, 1–60) a la izquierda y, plegados a la derecha, Tono, Idiomas, Dónde
 * atiende (canales de `listChannels`, G543), Atención humana (`human_hours` por día) y Qué puede hacer (`listAssistantTools`,
 * agrupadas por `module`, con el nombre corto `label` de cada herramienta; G537, G592, G688). Todo va al borrador. La
 * caducidad del modo humano (G446) es de Ajustes del buzón: aquí solo se enseña, con «Cambiar en el Buzón» (G689).
 */
export function Configuracion({ datos, ops, negocio, movil, navegar }: PropsApartado) {
    const b = datos.borrador.datos;
    const lock = !datos.editar;
    const [abierto, setAbierto] = useState<string | null>(null);
    const [tramo, setTramo] = useState<{ dia: number; indice: number | null; from: string; to: string; probado: boolean } | null>(null);
    const [herramientas, setHerramientas] = useState<Herramienta[] | null>(null);
    const [sinIdioma, setSinIdioma] = useState(false);

    useEffect(() => {
        let vivo = true;
        ops.listAssistantTools(negocio)
            .then((r) => { if (vivo) { setHerramientas(r.data); } })
            .catch(() => { if (vivo) { setHerramientas([]); } });
        return () => {
            vivo = false;
        };
    }, [ops, negocio]);

    useCabecera({
        ayuda: AYUDA.configuracion,
        acciones: datos.editar && datos.estado.has_unpublished_changes ? [{ clave: 'probar', texto: 'Probar y publicar', icono: 'arrow-right', secundaria: true, onClick: () => { datos.irA('probar'); } }] : [],
    });

    const pliega = (clave: string) => () => { setAbierto((a) => (a === clave ? null : clave)); };
    const horas = b.human_hours ?? [];
    const dias = tramosPorDia(horas);
    const canales = (datos.canales ?? []).filter((c) => c.status !== 'disconnected');
    const ids = b.channel_ids ?? [];
    const conectados = canales.filter(admiteAsistente);
    const zona = horaDe(null);
    const resumenCan = resumenCanales(ids, canales);
    const resumenAt = resumenAtencion(horas, zona);

    const guardarTramo = () => {
        if (tramo === null) {
            return;
        }
        const otros = (dias[tramo.dia] ?? []).filter((_, j) => j !== tramo.indice);
        if (errorDeTramo(tramo, otros) !== '') {
            setTramo({ ...tramo, probado: true });
            return;
        }
        const nuevo: Tramo = { weekday: tramo.dia + 1, from: tramo.from, to: tramo.to };
        datos.cambiar((c) => ({ ...c, human_hours: [...(c.human_hours ?? []).filter((t) => t.weekday !== tramo.dia + 1), ...otros, nuevo] }));
        setTramo(null);
    };

    const quitarTramo = () => {
        if (tramo === null || tramo.indice === null) {
            return;
        }
        const otros = (dias[tramo.dia] ?? []).filter((_, j) => j !== tramo.indice);
        datos.cambiar((c) => ({ ...c, human_hours: [...(c.human_hours ?? []).filter((t) => t.weekday !== tramo.dia + 1), ...otros] }));
        setTramo(null);
    };

    const grupos = gruposDeHerramientas(herramientas ?? []);
    const nAcciones = grupos.filter((g) => g.disponible).reduce((n, g) => n + g.herramientas.filter((h) => h.available).length, 0);
    const errorTramo = tramo === null ? '' : errorDeTramo(tramo, (dias[tramo.dia] ?? []).filter((_, j) => j !== tramo.indice));

    return (
        <div className="rv-as-config">
            <section className="rv-as-tarjeta rv-as-presenta" aria-label="Cómo se presenta">
                <div className="rv-as-eyebrow">
                    <span>Cómo se presenta</span>
                    <small>Es lo que ven tus clientes cuando les escribe.</small>
                </div>
                <Campo
                    etiqueta="Nombre del asistente"
                    valor={b.assistant_name}
                    max={60}
                    mayusculas="words"
                    nombre="nombre-asistente"
                    ayuda="Por ejemplo, Lola o «Asistente de Caseta Embrujo»."
                    error={b.assistant_name.trim() === '' ? 'Ponle un nombre: es como se presenta.' : null}
                    bloqueado={lock}
                    onChange={(v) => { datos.cambiar((c) => ({ ...c, assistant_name: v })); }}
                />
            </section>

            <div className="rv-as-pliegues">
                <Pliegue icono="message-circle" titulo="Tono" resumen={nombreTono(b.tone)} abierto={abierto === 'tono'} onAlternar={pliega('tono')}>
                    <p className="rv-as-ayuda">Cómo escribe a tus clientes, en todos los canales y en todos los idiomas.</p>
                    <div className="rv-as-opciones" role="radiogroup" aria-label="Tono">
                        {TONOS.map((t) => (
                            <OpcionRadio key={t.valor} elegida={b.tone === t.valor} bloqueada={lock} titulo={t.etiqueta} texto={t.texto} onElegir={() => { if (b.tone !== t.valor) { datos.cambiar((c) => ({ ...c, tone: t.valor })); } }}>
                                <span className="rv-as-ejemplo">{t.ejemplo}</span>
                            </OpcionRadio>
                        ))}
                    </div>
                </Pliegue>

                <Pliegue icono="languages" titulo="Idiomas" resumen={resumenIdiomas(b.locales)} chip={sinIdioma ? 'Al menos uno' : ''} abierto={abierto === 'idiomas' || sinIdioma} onAlternar={pliega('idiomas')}>
                    <p className="rv-as-ayuda">Responde en el idioma en que le escriben, si está en la lista.</p>
                    <div className="rv-as-pastillas">
                        {IDIOMAS.map((l) => {
                            const on = b.locales.includes(l.valor);
                            return (
                                <button
                                    key={l.valor}
                                    type="button"
                                    className={`rv-as-pastilla${on ? ' rv-as-pastilla--on' : ''}`}
                                    aria-pressed={on}
                                    disabled={lock}
                                    onClick={() => {
                                        if (on && b.locales.length === 1) {
                                            setSinIdioma(true);
                                            setTimeout(() => { setSinIdioma(false); }, 2600);
                                            return;
                                        }
                                        datos.cambiar((c) => ({ ...c, locales: on ? c.locales.filter((x) => x !== l.valor) : [...c.locales, l.valor] }));
                                    }}
                                >
                                    <Icono nombre={on ? 'check' : 'plus'} />
                                    {l.etiqueta}
                                </button>
                            );
                        })}
                    </div>
                    {sinIdioma && <p className="rv-as-error" role="alert">Deja al menos un idioma.</p>}
                </Pliegue>

                <Pliegue icono="plug" titulo="Dónde atiende" resumen={resumenCan.texto} chip={resumenCan.chip} abierto={abierto === 'canales'} onAlternar={pliega('canales')}>
                    {conectados.length === 0 ? (
                        <div className="rv-as-vacio-pequeno">
                            <span>Todavía no hay canales conectados</span>
                            <small>Conecta WhatsApp, Telegram o el chat de tu web para que el asistente pueda atender.</small>
                            <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { navegar('/canales'); }}>
                                Ir a Canales
                            </Boton>
                        </div>
                    ) : (
                        <>
                            <Interruptor
                                etiqueta="También en los canales nuevos"
                                ayuda="Los que conectes más adelante, desde el primer mensaje."
                                valor={ids.length === 0}
                                bloqueado={lock}
                                onChange={(v) => { datos.cambiar((c) => ({ ...c, channel_ids: v ? [] : conectados.map((x) => x.id) })); }}
                            />
                            {canales.map((c, i) => {
                                const admite = admiteAsistente(c);
                                return (
                                    <Interruptor
                                        key={c.id}
                                        icono={iconoCanal(c)}
                                        etiqueta={nombreCanal(c)}
                                        ayuda={c.display_identifier}
                                        valor={admite && (ids.length === 0 || ids.includes(c.id))}
                                        bloqueado={lock || !admite}
                                        chip={admite ? undefined : 'Al buzón'}
                                        linea={i < canales.length - 1 ? 'abajo' : 'ninguna'}
                                        onChange={(v) => {
                                            datos.cambiar((x) => {
                                                const ahora = (x.channel_ids ?? []).length === 0 ? conectados.map((k) => k.id) : [...(x.channel_ids ?? [])];
                                                return { ...x, channel_ids: v ? [...new Set([...ahora, c.id])] : ahora.filter((k) => k !== c.id) };
                                            });
                                        }}
                                    />
                                );
                            })}
                            <p className="rv-as-ayuda">En los canales que apagues, los mensajes van directos al buzón.</p>
                        </>
                    )}
                </Pliegue>

                <Pliegue icono="headset" titulo="Atención humana" resumen={resumenAt.texto} chip={resumenAt.chip} abierto={abierto === 'atencion'} onAlternar={pliega('atencion')}>
                    <p className="rv-as-ayuda">Cuándo hay alguien del equipo en el buzón. Fuera de este horario, si pasa a una persona, el asistente avisa de cuándo le responderéis.</p>
                    {horas.length === 0 && <p className="rv-as-ayuda rv-as-ayuda--ambar">Cuando pase a una persona, el asistente no podrá decir cuándo le responderéis. Abre los días en que alguien mira el buzón.</p>}
                    <div className="rv-as-dias">
                        {DIAS.map((nombre, i) => {
                            const tr = dias[i] ?? [];
                            return (
                                <div key={nombre} className="rv-as-dia">
                                    <Interruptor
                                        etiqueta={nombre}
                                        ariaLabel={`${nombre}: atención humana`}
                                        ayuda={movil ? (tr.length === 0 ? 'Sin atención' : textoTramosDelDia(tr)) : undefined}
                                        valor={tr.length > 0}
                                        bloqueado={lock}
                                        linea="ninguna"
                                        onChange={(v) => {
                                            if (v) {
                                                setTramo({ dia: i, indice: null, from: '10:00', to: '14:00', probado: false });
                                            } else {
                                                datos.cambiar((c) => ({ ...c, human_hours: (c.human_hours ?? []).filter((t) => t.weekday !== i + 1) }));
                                            }
                                        }}
                                    />
                                    {!movil && (
                                        <div className="rv-as-tramos">
                                            {tr.length === 0 && <span className="rv-as-sin">Sin atención</span>}
                                            {tr.map((t, j) => (
                                                <button key={`${t.from}-${t.to}`} type="button" className="rv-as-tramo" disabled={lock} aria-label={`${nombre}, de ${t.from} a ${t.to}${lock ? '' : ': cambiar'}`} onClick={() => { setTramo({ dia: i, indice: j, from: t.from, to: t.to, probado: false }); }}>
                                                    {`${t.from} a ${t.to}`}
                                                </button>
                                            ))}
                                            {!lock && tr.length > 0 && tr.length < 3 && (
                                                <button type="button" className="rv-as-enlace" onClick={() => { setTramo({ dia: i, indice: null, from: '17:00', to: '20:00', probado: false }); }}>
                                                    <Icono nombre="plus" />
                                                    Añadir tramo
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {datos.caducidad !== null && (
                        <div className="rv-as-caducidad">
                            <span className="rv-as-caducidad__texto">
                                <strong>Cuándo vuelve el asistente</strong>
                                <small>{`Cuando alguien del equipo toma una conversación, el asistente no contesta hasta que se la devuelven o pasan ${textoCaducidad(datos.caducidad)} sin mensajes.`}</small>
                            </span>
                            <Campo etiqueta="Vuelve a contestar tras" valor={`${textoCaducidad(datos.caducidad)} sin mensajes`} nombre="caducidad-modo-humano" bloqueado onChange={() => undefined} />
                            <span className="rv-as-caducidad__pie">
                                <Icono nombre="pin" />
                                <span>Es un ajuste del buzón. En el Buzón también se puede dejar fijada una conversación hasta devolverla.</span>
                            </span>
                            <button type="button" className="rv-as-enlace" onClick={() => { navegar('/buzon?panel=ajustes'); }}>
                                Cambiar en el Buzón
                                <Icono nombre="arrow-right" />
                            </button>
                        </div>
                    )}
                </Pliegue>

                <Pliegue
                    icono="shield-check"
                    titulo="Qué puede hacer"
                    resumen={herramientas === null ? 'Cargando…' : `${String(nAcciones)} ${nAcciones === 1 ? 'acción' : 'acciones'}, las mismas que tus clientes en tu web`}
                    abierto={abierto === 'herramientas'}
                    onAlternar={pliega('herramientas')}
                >
                    <p className="rv-as-ayuda">Tu asistente solo puede hacer lo que tus clientes pueden hacer en tu web, con sus mismos permisos. Depende de lo que tengas contratado; no se cambia aquí.</p>
                    {grupos.map((g) => (
                        <div key={g.modulo} className={`rv-as-grupo${g.disponible ? '' : ' rv-as-grupo--apagado'}`}>
                            <div className="rv-as-grupo__cab">
                                <span className={`rv-as-baldosa${g.disponible ? ' rv-as-baldosa--tinta' : ''}`}>
                                    <Icono nombre={g.icono} />
                                </span>
                                <strong>{g.nombre}</strong>
                                {g.base && <Chip estado="confirmed">Siempre incluido</Chip>}
                                {!g.disponible && <Chip estado="neutral">Sin contratar</Chip>}
                            </div>
                            {g.herramientas.map((h) => (
                                <div key={h.code} className="rv-as-herramienta">
                                    <Icono nombre={iconoDeEfecto(h, g)} />
                                    <span>
                                        <strong>{nombreDeHerramienta(h)}</strong>
                                        <small>{lineaDeHerramienta(h, g)}</small>
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </Pliegue>
            </div>

            <PanelLateral
                abierto={tramo !== null}
                onCerrar={() => { setTramo(null); }}
                movil={movil}
                eyebrow="Atención humana"
                icono="headset"
                titulo={tramo === null ? '' : tramo.indice === null ? `Nuevo tramo del ${(DIAS[tramo.dia] ?? '').toLocaleLowerCase('es')}` : `${DIAS[tramo.dia] ?? ''} de ${tramo.from} a ${tramo.to}`}
                accion={tramo?.indice === null ? 'Añadir' : 'Guardar'}
                onAccion={guardarTramo}
            >
                {tramo !== null && (
                    <div className="rv-as-panel">
                        <Desplegable etiqueta="Desde" valor={tramo.from} opciones={HORAS_DEL_DIA.map((h) => ({ valor: h, texto: h }))} onChange={(v) => { setTramo({ ...tramo, from: v }); }} />
                        <Desplegable etiqueta="Hasta" valor={tramo.to} opciones={HORAS_DEL_DIA.map((h) => ({ valor: h, texto: h }))} onChange={(v) => { setTramo({ ...tramo, to: v }); }} />
                        <small className="rv-as-ayuda">{zona.charAt(0).toLocaleUpperCase('es') + zona.slice(1)}, la del negocio</small>
                        {(tramo.probado || errorTramo.startsWith('Se junta')) && errorTramo !== '' && <p className="rv-as-error" role="alert">{errorTramo}</p>}
                        {tramo.indice !== null && (
                            <Boton tipo="destructivo" icono="trash-2" movil={movil} onClick={quitarTramo}>
                                Quitar este tramo
                            </Boton>
                        )}
                    </div>
                )}
            </PanelLateral>
        </div>
    );
}

/** RV Desplegable: una tarjeta plegada con su resumen de una línea. */
export function Pliegue({ icono, titulo, resumen, chip = '', abierto, onAlternar, children }: { icono: NombreIcono; titulo: string; resumen: string; chip?: string; abierto: boolean; onAlternar: () => void; children: ReactNode }) {
    return (
        <section className={`rv-as-tarjeta rv-as-pliegue${abierto ? ' rv-as-pliegue--abierto' : ''}`}>
            <button type="button" className="rv-as-pliegue__cab" aria-expanded={abierto} onClick={onAlternar}>
                <span className="rv-as-baldosa">
                    <Icono nombre={icono} />
                </span>
                <span className="rv-as-pliegue__texto">
                    <strong>
                        {titulo}
                        {chip !== '' && <Chip estado="pending">{chip}</Chip>}
                    </strong>
                    <span>{resumen}</span>
                </span>
                <Icono nombre="chevron-down" className="rv-as-pliegue__flecha" />
            </button>
            {abierto && <div className="rv-as-pliegue__cuerpo">{children}</div>}
        </section>
    );
}
